import { Button } from "@/shadcnComponents/ui/button";
import { Input } from "@/shadcnComponents/ui/input";
import { Label } from "@/shadcnComponents/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import { useEffect, useState } from "react";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import { Loader } from "lucide-react";
import mixpanel from "mixpanel-browser";
import axios from "axios";
import { initialNewCampaignFormData } from "../DashBoard/PromoteAd/Helpers/Helper";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  Eye,
  Key,
  type LucideIcon,
  MessageSquare,
  Percent,
  Shield,
  Sparkles,
  TrendingUp,
  Unlock,
} from "lucide-react";
import React from "react";
import { ScreenAlert } from "@/Pages/SmallScreenAlert/ScreenAlert";

interface SampleFormData {
  name: string;
  email: string;
  company: string;
}

export function SampleHome() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SampleFormData | null>(null);
  const [loading, setLoading] = useState(false);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  }

  async function handleSubmit() {
    //validate the data here
    if (!formData?.name || !formData?.company) {
      toast({
        title: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_SERVER_URL}/brand/sample/SignUp`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      }
    );
    if (!response.ok) {
      setLoading(false);
      toast({
        title: "Something went wrong",
        description: "Please try again later",
        variant: "destructive",
      });
      return;
    }

    const data = await response.json();
    localStorage.setItem("jwtToken", data.token);
    localStorage.setItem("UserType", "Brand");

    // Identify and track the user in Mixpanel
    mixpanel.identify(formData?.email);
    mixpanel.people.set_once({
      $email: formData?.email,
      isBrand: true,
    });

    // Redirect to the sample campaign page with query params
    try {
      const res = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_SERVER_URL
        }/collabs/brand/createBrandCollabs`,
        {
          step: 0,
          data: {
            ...initialNewCampaignFormData,
            campaignInfo: {
              ...initialNewCampaignFormData.campaignInfo,
              campaignName: "Sample Campaign",
              campaignDescription: "This is a dummy campaign description",
              isSampleCampaign: true,
            },
          },
        },
        {
          headers: {
            jwtToken: localStorage.getItem("jwtToken"),
          },
        }
      );

      if (res?.status === 200 && res?.data?.success) {
        toast({
          title: "Success",
          description: "Campaign created successfully",
        });
        setLoading(false);
        navigate(
          `/Brand/DashBoard/AllCampaign/Draft/${res?.data?.collab?._id}?sampleCampaign=true`
        );
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }

    // redirect(`/sample-campaign?${params.toString()}`)
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-4 md:p-24">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">
            Explore Sample Campaign
          </CardTitle>
          <CardDescription>
            Fill in your details to view our sample campaign
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              value={formData?.name}
              onChange={handleChange}
              name="name"
              placeholder="Enter your name"
              required
            />
          </div>
          {/* <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              value={formData?.email}
              onChange={handleChange}
              type="email"
              placeholder="Enter your email"
              required
            />
          </div> */}
          <div className="space-y-2">
            <Label htmlFor="company">Company Name</Label>
            <Input
              id="company"
              name="company"
              value={formData?.company}
              onChange={handleChange}
              placeholder="Enter your company name"
              required
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button onClick={handleSubmit} className="w-full">
            {loading ? <Loader className="animate-spin" /> : "Submit"}
          </Button>
        </CardFooter>
      </Card>
    </main>
  );
}

interface LoadingStep {
  icon: LucideIcon;
  title: string;
  description: string;
  duration: number;
}

export default function ExperiencePage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [brandName, setBrandName] = useState("");
  const [loading, setLoading] = useState(false);
  const [, setIsLoading] = useState(false);
  const [accessStep, setAccessStep] = useState(0);
  const [currentLoadingStep, setCurrentLoadingStep] = useState(0);
  const [progress, setProgress] = useState(0);

  // Define the loading steps sequence
  const loadingSteps: LoadingStep[] = [
    {
      icon: Shield,
      title: "Preparing Secure Environment",
      description: "Setting up a secure sandbox for your experience...",
      duration: 3000,
    },
    {
      icon: Key,
      title: "Creating Temporary Access",
      description: "Generating special access keys without requiring login...",
      duration: 3000,
    },
    {
      icon: Eye,
      title: "Unlocking Sample Features",
      description:
        "Preparing demo data and sample features for you to explore...",
      duration: 3000,
    },
    {
      icon: Unlock,
      title: "Finalizing No-Login Access",
      description: "Almost there! Setting up your personalized experience...",
      duration: 2000,
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandName) return;
    const formData = {
      company: brandName,
    };
    setLoading(true);
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_SERVER_URL}/brand/sample/SignUp`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      }
    );
    if (!response.ok) {
      setLoading(false);
      toast({
        title: "Something went wrong",
        description: "Please try again later",
        variant: "destructive",
      });
      return;
    }
    const data = await response.json();
    localStorage.setItem("jwtToken", data.token);
    localStorage.setItem("UserType", "Brand");

    setIsLoading(true);
    setAccessStep(1);
    setCurrentLoadingStep(0);
    setProgress(0);

    // Start the loading sequence
    runLoadingSequence();
  };

  const runLoadingSequence = () => {
    let currentStep = 0;
    const totalDuration = loadingSteps.reduce(
      (total, step) => total + step.duration,
      0
    );
    let elapsedTime = 0;

    // Update progress every 50ms
    const progressInterval = setInterval(() => {
      elapsedTime += 50;
      const newProgress = Math.min(
        100,
        Math.floor((elapsedTime / totalDuration) * 100)
      );
      setProgress(newProgress);

      // Check if we should move to the next step
      const currentStepDuration = loadingSteps
        .slice(0, currentStep + 1)
        .reduce((total, step) => total + step.duration, 0);
      if (
        elapsedTime >= currentStepDuration &&
        currentStep < loadingSteps.length - 1
      ) {
        currentStep++;
        setCurrentLoadingStep(currentStep);
      }

      // End of sequence
      if (newProgress >= 100) {
        clearInterval(progressInterval);
        setTimeout(() => {
          setAccessStep(2); // Move to the access granted screen

          setTimeout(async () => {
            try {
              const res = await axios.post(
                `${
                  import.meta.env.VITE_BACKEND_SERVER_URL
                }/collabs/brand/createBrandCollabs`,
                {
                  step: 0,
                  data: {
                    ...initialNewCampaignFormData,
                    campaignInfo: {
                      ...initialNewCampaignFormData.campaignInfo,
                      campaignName: "Sample Campaign",
                      campaignDescription:
                        "This is a dummy campaign description",
                      isSampleCampaign: true,
                    },
                  },
                },
                {
                  headers: {
                    jwtToken: localStorage.getItem("jwtToken"),
                  },
                }
              );

              if (res?.status === 200 && res?.data?.success) {
                toast({
                  title: "Success",
                  description: "Campaign created successfully",
                });
                setLoading(false);
                navigate(
                  `/Brand/DashBoard/AllCampaign/Draft/${res?.data?.collab?._id}?sampleCampaign=true`
                );
              }
            } catch (error) {
              console.log(error);
            }
          }, 2500);
        }, 1000);
      }
    }, 50);
  };

  // Prevent scrolling when in full-screen experience mode
  useEffect(() => {
    if (accessStep > 0) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [accessStep]);

  return (
    <>
      {window.innerWidth > 760 ? (
        <div className="h-screen w-screen overflow-hidden bg-white flex flex-col">
          {/* Full-width Header */}
          <header className="w-full py-4 px-6 md:px-10 flex justify-between items-center border-b">
            <div className="flex items-center gap-2">
              <img
                src="/collabLogo.png"
                alt="logo"
                width={132}
                height={132}
                className="border-2 border-white"
              />
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/brand/login"
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Login
              </Link>
              <Button
                onClick={() => {
                  navigate("/brand/signup");
                }}
                size="sm"
                className="bg-red-600 hover:bg-red-700"
              >
                Sign Up
              </Button>
            </div>
          </header>

          {/* 80% Width Container for Main Content */}
          <div className="flex-1 flex items-center justify-center">
            <div className="w-[80%] h-[85%] bg-white shadow-xl rounded-xl overflow-hidden">
              {/* Main Content - Left/Right Layout */}
              <main className="h-full flex flex-col md:flex-row">
                {/* Left Section - Value Proposition */}
                <div className="w-full md:w-1/2 p-6 md:p-10 flex flex-col justify-center">
                  <div className="max-w-xl">
                    <div className="inline-flex items-center gap-2 bg-red-50 px-3 py-1.5 rounded-full mb-4">
                      <Sparkles className="h-3.5 w-3.5 text-red-500" />
                      <span className="text-xs font-medium text-red-700">
                        1st Time Ever
                      </span>
                    </div>

                    <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                      Influencer Marketing 2.0
                    </h1>

                    <p className="text-gray-600 mb-8">
                      Get a hands-on feel, from discovery to execution,
                      hassle-free
                    </p>

                    {/* Features Section - From Image */}
                    <div className="grid grid-cols-1 gap-6 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="h-4 w-4 text-red-500" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold">
                            ZERO follow ups
                          </h3>
                          <p className="text-xs text-gray-600">
                            No calls, chats or emails to the influencers needed
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                          <TrendingUp className="h-4 w-4 text-red-500" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold">
                            Verified influencer data
                          </h3>
                          <p className="text-xs text-gray-600">
                            Straight from their social accounts to you. No
                            middle layer, no manipulation
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                          <Percent className="h-4 w-4 text-red-500" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold">
                            Pay-for-what-you-get model
                          </h3>
                          <p className="text-xs text-gray-600">
                            Only pay for results. No over-spending, no
                            negotiations.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Section - Form & Visual */}
                <div className="w-full md:w-1/2 bg-gradient-to-br from-red-50 to-white flex items-center justify-center p-6 md:p-10">
                  <div className="w-full max-w-md">
                    <Card className="bg-white border border-gray-100 shadow-xl">
                      <CardContent className="p-6">
                        <div className="text-center mb-6">
                          <h2 className="text-xl font-bold mb-2">
                            Experience For Yourself
                          </h2>
                          <p className="text-sm text-gray-600">
                            Run a sample campaign, start to end
                          </p>
                        </div>

                        <form className="space-y-5" onSubmit={handleSubmit}>
                          <div className="space-y-2">
                            <label
                              htmlFor="brandName"
                              className="text-sm font-medium text-gray-700"
                            >
                              Tell us your name
                            </label>
                            <Input
                              id="brandName"
                              placeholder="(For a personalized experience)"
                              className="h-11 text-base"
                              value={brandName}
                              onChange={(e) => setBrandName(e.target.value)}
                              required
                            />
                          </div>

                          <Button
                            type="submit"
                            className="w-full h-11 text-base bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transition-all duration-300"
                          >
                            {loading ? (
                              <Loader className="animate-spin" />
                            ) : (
                              <>
                                Begin
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </>
                            )}
                          </Button>
                        </form>

                        <div className="mt-6 pt-6 border-t border-gray-100">
                          <div className="flex items-center gap-3">
                            <div className="flex -space-x-2">
                              <img
                                src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/sampleBrand1.png"
                                alt="User"
                                width={32}
                                height={32}
                                className="rounded-full border-2 border-white"
                              />
                              <img
                                src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/sampleBrand2.png"
                                alt="User"
                                width={32}
                                height={32}
                                className="rounded-full border-2 border-white"
                              />
                              <img
                                src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/sampleBrand3.png"
                                alt="User"
                                width={32}
                                height={32}
                                className="rounded-full border-2 border-white"
                              />
                            </div>
                            <p className="text-xs text-gray-600">
                              <span className="font-semibold">350+ brands</span>{" "}
                              are in the queue already
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="mt-4 text-center">
                      <p className="text-xs text-gray-500">
                        No login needed. No credit card required.
                      </p>
                    </div>
                  </div>
                </div>
              </main>
            </div>
          </div>

          {/* Full-Screen Loading Experience */}
          {accessStep > 0 && (
            <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
              {/* Enhanced Loading Animation with Steps */}
              {accessStep === 1 && (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-b from-white to-red-50">
                  <div className="text-center max-w-md mx-auto px-4">
                    {/* Current step icon */}
                    <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                      {React.createElement(
                        loadingSteps[currentLoadingStep].icon,
                        {
                          className: "h-10 w-10 text-red-500",
                        }
                      )}
                    </div>

                    {/* Step title and description */}
                    <h3 className="text-2xl font-bold mb-3">
                      {loadingSteps[currentLoadingStep].title}
                    </h3>
                    <p className="text-gray-600 mb-8">
                      {loadingSteps[currentLoadingStep].description}
                    </p>

                    {/* Progress bar */}
                    <div className="w-full bg-white/50 h-3 rounded-full overflow-hidden mb-8">
                      <div
                        className="bg-red-500 h-full transition-all duration-300 ease-out"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>

                    {/* Special access message */}
                    <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-100">
                      <p className="text-sm text-red-800">
                        <span className="font-semibold">Note:</span> You’ll have
                        access to all core features to explore the platform
                        end-to-end. Some functions tied to live campaigns (like
                        selecting real influencers) will be limited for now.
                      </p>
                    </div>

                    {/* Step indicators */}
                    <div className="mt-8 flex justify-center gap-2">
                      {loadingSteps.map((_, index) => (
                        <div
                          key={index}
                          className={`h-2 w-2 rounded-full ${
                            index === currentLoadingStep
                              ? "bg-red-500"
                              : index < currentLoadingStep
                              ? "bg-red-300"
                              : "bg-gray-200"
                          }`}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Access Granted - Full Screen Experience */}
              {accessStep === 2 && (
                <div className="w-full h-full bg-gradient-to-br from-red-600 to-red-800 text-white flex items-center justify-center overflow-hidden">
                  <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full">
                      {/* Animated particles/sparkles effect */}
                      {Array.from({ length: 20 }).map((_, i) => (
                        <div
                          key={i}
                          className="absolute rounded-full bg-white/20 animate-pulse"
                          style={{
                            width: `${Math.random() * 10 + 5}px`,
                            height: `${Math.random() * 10 + 5}px`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            animationDuration: `${Math.random() * 3 + 2}s`,
                            animationDelay: `${Math.random() * 2}s`,
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>

                  <div className="relative z-10 text-center max-w-2xl mx-auto px-4">
                    {/* Sample Mode Banner - Visible at the top */}
                    {/* <div className="absolute top-4 left-0 right-0">
                  <SampleModeBanner />
                </div> */}

                    <div className="h-24 w-24 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-8 backdrop-blur-sm">
                      <Sparkles className="h-12 w-12 text-white" />
                    </div>

                    <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
                      No-Login Access Granted
                    </h2>

                    <p className="text-xl md:text-2xl mb-4 text-white/90">
                      Hello, <span className="font-bold">{brandName}</span>!
                    </p>

                    <p className="text-lg mb-6 max-w-xl mx-auto">
                      You'll be redirected to your campaign experience in just a
                      moment....
                    </p>

                    <div className="bg-white/10 hidden backdrop-blur-sm rounded-lg p-4 mb-8 max-w-lg mx-auto">
                      <h3 className="font-semibold text-lg mb-2">
                        What you can explore:
                      </h3>
                      <ul className="text-left text-sm space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>
                            Campaign creation interface with sample data
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>
                            Influencer matching algorithm with demo profiles
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>
                            Analytics dashboard with simulated performance
                            metrics
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div className=" flex-col hidden md:flex-row gap-4 justify-center">
                      <Button
                        size="lg"
                        className="bg-white text-red-600 hover:bg-white/90 h-12 px-6"
                      >
                        Explore Sample Dashboard
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        className="border-white text-black hover:text-white hover:bg-white/10 h-12 px-6"
                      >
                        Watch Quick Tour
                      </Button>
                    </div>

                    <div className="mt-8 flex justify-center items-center gap-2">
                      <div className="animate-pulse">
                        <ArrowRight className="h-5 w-5" />
                      </div>
                      <p className="text-sm text-white/80">
                        Don’t miss the Detailed Influencer Insights and the AI
                        performance report
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <ScreenAlert />
      )}
    </>
  );
}
